"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import type { T_TEST_CALCULATOR_STRINGS } from "@/components/tools/t-test-calculator";
import { t } from "@/lib/i18n/interpolate";
import {
  type Alternative,
  apaP,
  fixed,
  formatDf,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import { pFor, type TTestResult } from "@/lib/tool-kit/stats-tests/t-test";

/** The t-test calculator's results: cards, decision, p-table, working and APA line. */

type Strings = typeof T_TEST_CALCULATOR_STRINGS;

function apaNote(result: TTestResult, strings: Strings): string {
  const notes = {
    "one-sample": strings.apaNoteOne,
    welch: strings.apaNoteWelch,
    student: strings.apaNoteStudent,
    paired: strings.apaNotePaired,
  } as const;
  return notes[result.kind];
}

function TTestWorking({
  r,
  fmt,
  strings,
}: {
  r: TTestResult;
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const level = fmt(r.level, { style: "percent", maximumFractionDigits: 1 });
  const [a, b] = r.samples;
  const ci = `${level} CI = ${n(r.kind === "one-sample" ? a.mean : r.difference)} ± ${n(r.tCritical)} × ${n(r.se)} = [${n(r.ciLow)}, ${n(r.ciHigh)}]`;
  if (r.kind === "one-sample") {
    return (
      <Working heading={strings.howCalculated}>
        <p>{`x̄ = ${n(a.mean)}, s = ${n(a.sd)}, n = ${fmt(a.n)}, μ₀ = ${n(r.mu0 ?? 0)}`}</p>
        <p>{`SE = s / √n = ${n(a.sd)} / √${fmt(a.n)} = ${n(r.se)}`}</p>
        <p>{`t = (x̄ − μ₀) / SE = ${n(r.difference)} / ${n(r.se)} = ${n(r.t)}, df = n − 1 = ${fmt(r.df)}`}</p>
        <p>{ci}</p>
        <p>{`d = (x̄ − μ₀) / s = ${n(r.difference)} / ${n(a.sd)} = ${n(r.effectSize)}`}</p>
      </Working>
    );
  }
  if (r.kind === "paired" && r.differences) {
    const d = r.differences;
    return (
      <Working heading={strings.howCalculated}>
        <p>{`${strings.differencesNote} d̄ = ${n(d.mean)}, s = ${n(d.sd)}, n = ${fmt(d.n)}`}</p>
        <p>{`SE = s / √n = ${n(d.sd)} / √${fmt(d.n)} = ${n(r.se)}`}</p>
        <p>{`t = d̄ / SE = ${n(d.mean)} / ${n(r.se)} = ${n(r.t)}, df = n − 1 = ${fmt(r.df)}`}</p>
        <p>{ci}</p>
        <p>{`dz = d̄ / s = ${n(d.mean)} / ${n(d.sd)} = ${n(r.effectSize)}`}</p>
      </Working>
    );
  }
  const head = `x̄₁ = ${n(a.mean)}, s₁ = ${n(a.sd)}, n₁ = ${fmt(a.n)}; x̄₂ = ${n(b.mean)}, s₂ = ${n(b.sd)}, n₂ = ${fmt(b.n)}`;
  if (r.kind === "student") {
    const sp = r.pooledSd ?? 0;
    return (
      <Working heading={strings.howCalculated}>
        <p>{head}</p>
        <p>{`sₚ = √(((n₁ − 1)s₁² + (n₂ − 1)s₂²) / (n₁ + n₂ − 2)) = √((${fmt(a.n - 1)} × ${n(a.sd)}² + ${fmt(b.n - 1)} × ${n(b.sd)}²) / ${fmt(r.df)}) = ${n(sp)}`}</p>
        <p>{`SE = sₚ × √(1/n₁ + 1/n₂) = ${n(sp)} × √(1/${fmt(a.n)} + 1/${fmt(b.n)}) = ${n(r.se)}`}</p>
        <p>{`t = (x̄₁ − x̄₂) / SE = ${n(r.difference)} / ${n(r.se)} = ${n(r.t)}, df = n₁ + n₂ − 2 = ${fmt(r.df)}`}</p>
        <p>{ci}</p>
        <p>{`d = (x̄₁ − x̄₂) / sₚ = ${n(r.difference)} / ${n(sp)} = ${n(r.effectSize)}`}</p>
      </Working>
    );
  }
  const [va, vb] = r.varianceTerms ?? [0, 0];
  return (
    <Working heading={strings.howCalculated}>
      <p>{head}</p>
      <p>{`SE = √(s₁²/n₁ + s₂²/n₂) = √(${n(va)} + ${n(vb)}) = ${n(r.se)}`}</p>
      <p>{`t = (x̄₁ − x̄₂) / SE = ${n(r.difference)} / ${n(r.se)} = ${n(r.t)}`}</p>
      <p>{`df = (s₁²/n₁ + s₂²/n₂)² / [(s₁²/n₁)²/(n₁ − 1) + (s₂²/n₂)²/(n₂ − 1)] = ${n(r.df)}`}</p>
      <p>{ci}</p>
      <p>{`d = (x̄₁ − x̄₂) / √((s₁² + s₂²)/2) = ${n(r.difference)} / ${n(Math.sqrt((a.sd ** 2 + b.sd ** 2) / 2))} = ${n(r.effectSize)}`}</p>
    </Working>
  );
}

export function TTestResults({
  r,
  alternative,
  alpha,
  strings,
  fmt,
}: {
  r: TTestResult;
  alternative: Alternative;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const p = pFor(r, alternative);
  const level = fmt(r.level, { style: "percent", maximumFractionDigits: 1 });
  const labels: Record<Alternative, string> = {
    "two-sided": strings.twoSided,
    less: strings.less,
    greater: strings.greater,
  };
  const apa = richTemplate(strings.apaTemplate, {
    df: formatDf(r.df, fmt),
    t: fixed(r.t, 2, fmt),
    p: apaP(p, fmt),
    d: fixed(r.effectSize, 2, fmt),
    level,
    low: fixed(r.ciLow, 2, fmt),
    high: fixed(r.ciHigh, 2, fmt),
  });
  const decision = t(
    p < alpha ? strings.decisionReject : strings.decisionKeep,
    {
      alpha: fmt(alpha),
      p: formatP(p, fmt),
    }
  );
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.tStatistic} value={n(r.t)} />
        <StatCard label={strings.df} value={formatStat(r.df, fmt)} />
        <StatCard
          emphasis
          label={t(strings.pValueFor, { alternative: labels[alternative] })}
          value={formatP(p, fmt)}
        />
        <StatCard
          label={
            r.kind === "one-sample"
              ? strings.meanMinusMu0
              : strings.meanDifference
          }
          value={n(r.difference)}
        />
        <StatCard label={strings.standardError} value={n(r.se)} />
        <StatCard
          label={t(
            r.kind === "one-sample" ? strings.ciMean : strings.ciDifference,
            { level }
          )}
          value={`[${n(r.ciLow)}, ${n(r.ciHigh)}]`}
        />
        <StatCard
          label={r.kind === "paired" ? strings.cohensDz : strings.cohensD}
          value={n(r.effectSize)}
        />
      </StatGrid>
      <Decision reject={p < alpha}>{decision}</Decision>
      <ResultTable
        caption={strings.pTableCaption}
        head={[
          strings.alternativeColumn,
          strings.exactColumn,
          strings.apaColumn,
        ]}
        rows={(["two-sided", "less", "greater"] as const).map((key) => ({
          key,
          cells: [
            labels[key],
            formatP(pFor(r, key), fmt),
            `p ${apaP(pFor(r, key), fmt)}`,
          ],
        }))}
      />
      <TTestWorking fmt={fmt} r={r} strings={strings} />
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={apaNote(r, strings)}
        parts={apa}
      />
    </section>
  );
}
